import { useState } from 'react'

import { Box, Checkbox, Group, Stack, Text } from '@mantine/core'
import { IconAsset } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  useAdminFund,
  useAdminFundAssets,
  useUpdateFundAssetNotable,
} from '@repo/common/queries/admin/fund'

import { TowerApprovalControl } from '@/components/ui/TowerApprovalControl'
import { TowerCard } from '@/components/ui/TowerCard'
import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'

import styles from './FundAssetsControl.module.css'

interface FundAssetRowData {
  id: string

  asset_id: string
  asset_name: string
  category: 'company' | 'external_fund' | 'cash'
  fission_ticker: string | null

  official_shares_held?: number
  official_price?: number
  official_value: number

  live_shares_held?: number
  live_price?: number
  live_value: number

  notable: boolean
}

interface FundAssetsControlProps {
  fundId: string
}

export function FundAssetsControl({ fundId }: FundAssetsControlProps) {
  const [sortBy, setSortBy] = useState<string | null>('asset_name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean>>(
    new Map(),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    data: fundAssetsData,
    isLoading: isLoadingAssets,
    error: assetsError,
    refetch: refetchFundAssets,
  } = useAdminFundAssets(fundId)

  const {
    data: fundData,
    isLoading: isLoadingFund,
    error: fundError,
  } = useAdminFund(fundId)

  const updateNotableMutation = useUpdateFundAssetNotable()

  const isLoading = isLoadingAssets || isLoadingFund
  const error = assetsError || fundError

  const handleNotableChange = (assetId: string, notable: boolean) => {
    const currentAsset = processedData.find(
      (asset) => asset.asset_id === assetId,
    )
    if (!currentAsset) return

    setPendingChanges((prev) => {
      const newChanges = new Map(prev)

      if (currentAsset.notable === notable) {
        newChanges.delete(assetId)
      } else {
        newChanges.set(assetId, notable)
      }

      return newChanges
    })
  }

  const handleSubmitChanges = async () => {
    setIsSubmitting(true)
    try {
      const promises = Array.from(pendingChanges.entries()).map(
        ([assetId, notable]) =>
          updateNotableMutation.mutateAsync({
            fundId,
            assetId,
            notable,
          }),
      )

      await Promise.all(promises)
      await refetchFundAssets()
      // setPendingChanges(new Map())
    } catch (err) {
      console.error('Failed to update notable status:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getEffectiveNotableValue = (
    assetId: string,
    originalValue: boolean,
  ): boolean => {
    return pendingChanges.has(assetId)
      ? pendingChanges.get(assetId)!
      : originalValue
  }

  const processedData: FundAssetRowData[] = []
  fundAssetsData?.fund.assets.nodes.forEach((fundAsset) => {
    const asset = fundAsset.asset

    if (fundAsset.latest_published_nav) {
      const officialNav = fundAsset.latest_official_nav
      const liveNav = fundAsset.live_nav
      const livePrice =
        liveNav && liveNav.shares_held > 0
          ? liveNav.shares_value_held / liveNav.shares_held
          : 0
      // const publishedPrice =
      //   publishedNav.shares_held > 0
      //     ? publishedNav.shares_value_held / publishedNav.shares_held
      //     : 0
      const officialPrice =
        officialNav && officialNav.shares_held > 0
          ? officialNav.shares_value_held / officialNav.shares_held
          : 0

      processedData.push({
        id: fundAsset.id,
        asset_id: asset.id,
        asset_name: asset.name,
        category: asset.category,
        fission_ticker: asset.fission_ticker,

        official_shares_held: fundAsset.latest_official_nav?.shares_held ?? 0,
        official_price: officialPrice,
        official_value: fundAsset.latest_official_nav?.shares_value_held ?? 0,

        live_shares_held: liveNav?.shares_held ?? 0,
        live_price: livePrice,
        live_value: liveNav?.shares_value_held ?? 0,

        notable: fundAsset.notable,
      })
    }
  })

  if (fundData) {
    processedData.push({
      id: 'cash',
      asset_id: 'cash',
      asset_name: 'Cash',
      category: 'cash',
      fission_ticker: null,

      // official_shares_held: 1,
      // official_price: fundData.latest_official_nav?.cash_total_value ?? 0,
      official_value: fundData.latest_official_nav?.cash_total_value ?? 0,

      // live_shares_held: 1,
      // live_price: fundData.live_nav?.cash_total_value ?? 0,
      live_value: fundData.live_nav?.cash_total_value ?? 0,

      notable: false,
    })
  }

  const assetsOnly = processedData.filter((item) => item.category !== 'cash')
  const cashRow = processedData.find((item) => item.category === 'cash')

  const sortedAssets = [...assetsOnly]
  if (sortBy) {
    sortedAssets.sort((a, b) => {
      const aValue = a[sortBy as keyof FundAssetRowData]
      const bValue = b[sortBy as keyof FundAssetRowData]
      if (
        aValue === null ||
        bValue === null ||
        aValue === undefined ||
        bValue === undefined
      )
        return aValue === null || aValue === undefined ? 1 : -1
      return (
        (aValue < bValue ? -1 : aValue > bValue ? 1 : 0) *
        (sortDirection === 'asc' ? 1 : -1)
      )
    })
  }

  const sortedData = cashRow ? [...sortedAssets, cashRow] : sortedAssets

  const columns: TowerDataTableColumn<FundAssetRowData>[] = [
    column({
      accessor: 'asset_name',
      title: 'Asset Name',
      sortable: true,
      render: (name: string, row) => (
        <Stack gap={0}>
          <Group gap="xs" align="center">
            <Text size="sm" fw={500}>
              {name}
            </Text>
            {row.category !== 'cash' && (
              <Group gap={4}>
                <Link
                  to="/fund/$id/record-transaction"
                  params={{ id: fundId }}
                  search={{ side: 'buy' as const, asset_id: row.asset_id }}
                  className={styles.action_link_wrapper}
                >
                  <Text className={`${styles.action_link} ${styles.buy_link}`}>
                    buy
                  </Text>
                </Link>
                <Text size="xs" c="dimmed" className={styles.action_separator}>
                  /
                </Text>
                <Link
                  to="/fund/$id/record-transaction"
                  params={{ id: fundId }}
                  search={{ side: 'sell' as const, asset_id: row.asset_id }}
                  className={styles.action_link_wrapper}
                >
                  <Text className={`${styles.action_link} ${styles.sell_link}`}>
                    sell
                  </Text>
                </Link>
              </Group>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {
              {
                external_fund: 'External Fund',
                company: 'Company',
                cash: '',
              }[row.category]
            }
          </Text>
        </Stack>
      ),
    }),
    column({
      accessor: 'official_shares_held',
      title: 'Shares',
      textAlign: 'right',
      sortable: true,
      groupId: 'official',
      groupHeader: 'Official',
      render: (shares?: number) => (
        <Text size="sm">
          {shares !== undefined &&
            new Intl.NumberFormat('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0,
            }).format(shares)}
        </Text>
      ),
    }),
    column({
      accessor: 'official_price',
      title: 'Price',
      textAlign: 'right',
      sortable: true,
      groupId: 'official',
      render: (price?: number) => (
        <Text size="sm">{price !== undefined && usdFormatter(price)}</Text>
      ),
    }),
    column({
      accessor: 'official_value',
      title: 'Value',
      textAlign: 'right',
      sortable: true,
      groupId: 'official',
      render: (value: number) => <Text size="sm">{usdFormatter(value)}</Text>,
    }),
    column({
      accessor: 'live_shares_held',
      title: 'Shares',
      textAlign: 'right',
      sortable: true,
      groupId: 'live',
      groupHeader: 'Live',
      render: (shares?: number) => (
        <Text size="sm">
          {shares !== undefined &&
            new Intl.NumberFormat('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0,
            }).format(shares)}
        </Text>
      ),
    }),
    column({
      accessor: 'live_price',
      title: 'Price',
      textAlign: 'right',
      sortable: true,
      groupId: 'live',
      render: (price?: number) => (
        <Text size="sm">{price !== undefined && usdFormatter(price)}</Text>
      ),
    }),
    column({
      accessor: 'live_value',
      title: 'Value',
      textAlign: 'right',
      sortable: true,
      groupId: 'live',
      render: (value: number) => <Text size="sm">{usdFormatter(value)}</Text>,
    }),
    mapColumn({
      title: 'Notable',
      textAlign: 'center',
      render: (_, row) => (
        <div>
          {row.category != 'cash' && (
            <Checkbox
              checked={getEffectiveNotableValue(row.asset_id, row.notable)}
              onChange={(event) => {
                handleNotableChange(row.asset_id, event.currentTarget.checked)
              }}
              disabled={isSubmitting}
            />
          )}
        </div>
      ),
    }),
  ]

  if (error) {
    return (
      <TowerCard
        title="Fund Assets"
        description="Current asset holdings and notable status"
        icon={<IconAsset />}
      >
        <Text c="red">Error loading fund assets</Text>
      </TowerCard>
    )
  }

  return (
    <TowerCard
      title="Fund Assets"
      description="Current asset holdings and notable status"
      icon={<IconAsset />}
    >
      <TowerDataTable
        data={sortedData}
        columns={columns}
        isLoading={isLoading}
        onSort={(_, accessor) => {
          if (!accessor || typeof accessor === 'function') return
          const columnKey = accessor as string
          if (sortBy === columnKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
          } else {
            setSortBy(columnKey)
            setSortDirection('asc')
          }
        }}
        emptyMessage="No assets found for this fund"
        withTableBorder
        withColumnBorders
        highlightOnHover
        striped
      />
      {pendingChanges.size > 0 && (
        <Box w="20em" ml="auto">
          <TowerApprovalControl
            buttonText={`Publish ${pendingChanges.size} Notable Change${
              pendingChanges.size > 1 ? 's' : ''
            }`}
            onApprove={handleSubmitChanges}
            successText="Changes published"
            onCompleted={() => {
              setPendingChanges(new Map())
            }}
            disabled={updateNotableMutation.isPending || isSubmitting}
          />
        </Box>
      )}
    </TowerCard>
  )
}
